'use client';

/**
 * LineItem Component
 *
 * Displays a single price line item with description, quantity, and amount.
 * Supports editable mode and different item types (fee, discount, tax, etc.).
 */

import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import type { PriceLineItem } from '../../types/pricingEngine';
import { formatCurrency } from '../../utils/formatters';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface LineItemProps {
  /** Line item data */
  item: PriceLineItem;
  /** Currency code */
  currency?: string;
  /** Locale for formatting */
  locale?: string;
  /** Whether the item is editable */
  editable?: boolean;
  /** Change handler for editable items */
  onChange?: (value: number) => void;
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Whether this is a summary row (subtotal/total) */
  isSummary?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledRowProps {
  $isDark: boolean;
  $type: PriceLineItem['type'];
  $isSummary: boolean;
  $neonAccent?: NeonAccent;
}

const StyledRow = styled(
  Box,
  transientOptions,
)<StyledRowProps>(({ theme, $isDark, $type, $isSummary, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    borderBottom: $isSummary ? 'none' : `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,

    // Summary row styles
    ...($isSummary && {
      borderTop: `1px solid ${neonStyles?.border || ($isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')}`,
      marginTop: theme.spacing(1),
      paddingTop: theme.spacing(2),
    }),

    // Total row styles
    ...($type === 'total' && {
      borderTop: `2px solid ${neonStyles?.base || ($isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)')}`,
      paddingTop: theme.spacing(2),
      marginTop: theme.spacing(0.5),
    }),
  };
});

const StyledInput = styled(
  InputBase,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.5, 1),
      borderRadius: 4,
      border: `1px solid ${neonStyles?.border || ($isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')}`,
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      textAlign: 'right',
      width: 80,
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',

      '&:hover': {
        borderColor: neonStyles?.border || ($isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'),
      },

      '&:focus': {
        borderColor: neonStyles?.base || theme.palette.primary.main,
        boxShadow: neonStyles ? `0 0 0 2px ${neonStyles.glow}` : `0 0 0 2px ${theme.palette.primary.main}25`,
        outline: 'none',
      },
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const LineItem: React.FC<LineItemProps> = ({
  item,
  currency = 'USD',
  locale = 'en-US',
  editable = false,
  onChange,
  neonAccent,
  isSummary = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [inputValue, setInputValue] = useState(item.amount.toString());

  const isItemEditable = editable && item.editable !== false;
  const isNegative = item.type === 'discount';
  const isBold = item.type === 'subtotal' || item.type === 'total';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleInputBlur = () => {
    const numValue = parseFloat(inputValue);
    if (!Number.isNaN(numValue) && onChange) {
      onChange(numValue);
    } else {
      setInputValue(item.amount.toString());
    }
  };

  const getAmountColor = () => {
    if (item.type === 'discount') {
      return theme.palette.success.main;
    }
    if (item.type === 'total' && neonAccent) {
      const neonStyles = getNeonStyles(neonAccent, isDark);
      return neonStyles.base;
    }
    return theme.palette.text.primary;
  };

  const formattedAmount = formatCurrency(isNegative ? -Math.abs(item.amount) : item.amount, currency, locale);

  return (
    <StyledRow $isDark={isDark} $type={item.type} $isSummary={isSummary} $neonAccent={neonAccent}>
      <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
        <Typography
          variant={isBold ? 'subtitle2' : 'body2'}
          sx={{
            fontWeight: isBold ? 600 : 400,
            color: theme.palette.text.primary,
          }}
        >
          {item.description}
        </Typography>
        {item.details && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: 'block',
              mt: 0.25,
            }}
          >
            {item.details}
          </Typography>
        )}
        {item.quantity > 1 && item.unit && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: 'block',
              mt: 0.25,
            }}
          >
            {item.quantity} {item.unit} @ {formatCurrency(item.unitPrice, currency, locale)}
          </Typography>
        )}
      </Box>

      <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
        {isItemEditable ? (
          <StyledInput
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            $isDark={isDark}
            $neonAccent={neonAccent}
            inputProps={{
              'aria-label': `Edit ${item.description} amount`,
            }}
          />
        ) : (
          <Typography
            variant={isBold ? 'subtitle1' : 'body2'}
            sx={{
              fontWeight: isBold ? 700 : 400,
              color: getAmountColor(),
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formattedAmount}
          </Typography>
        )}
      </Box>
    </StyledRow>
  );
};

LineItem.displayName = 'LineItem';

export default LineItem;
